import { useEffect, useState } from 'react';
import { X, Loader, Apple } from 'lucide-react';
import { searchCiqual, searchOff, applyNutrition } from '../../../utils/nutritionApi';
import type {
  CiqualMatchCandidate,
  OffMatchCandidate,
  IngredientNutrition,
  NutritionPer100g,
} from '../../../types/nutrition';
import { emptyIngredientNutrition, emptyNutritionPer100g } from '../../../types/nutrition';

interface NutritionalFactModalProps {
  ingredientName: string;
  ingredientId?: string;
  currentNutrition?: IngredientNutrition | null;
  onClose: () => void;
  onApply: (nutrition: IngredientNutrition) => void;
}

type Step = 'ciqual' | 'off' | 'manual';

function MacroLine({ n }: { n: NutritionPer100g | CiqualMatchCandidate['nutrition'] }) {
  return (
    <p className="text-xs text-gray-500 mt-1">
      {n.kcal ?? '—'} kcal · P {n.protein_g ?? '—'}g · G {n.carbs_g ?? '—'}g · L {n.fat_g ?? '—'}g ·
      sel {n.salt_g ?? '—'}g
    </p>
  );
}

export function NutritionalFactModal({
  ingredientName,
  ingredientId,
  currentNutrition,
  onClose,
  onApply,
}: NutritionalFactModalProps) {
  const [step, setStep] = useState<Step>('ciqual');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ciqualCandidates, setCiqualCandidates] = useState<CiqualMatchCandidate[]>([]);
  const [offCandidates, setOffCandidates] = useState<OffMatchCandidate[]>([]);
  const [searched, setSearched] = useState(false);
  const [manual, setManual] = useState<NutritionPer100g>(
    currentNutrition?.per100g || emptyNutritionPer100g()
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await searchCiqual(ingredientName, 20);
        if (cancelled) return;
        setCiqualCandidates(res.candidates);
        setSearched(true);
        setStep('ciqual');
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
          setSearched(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ingredientName]);

  const runOffSearch = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await searchOff(ingredientName, 5);
      setOffCandidates(res.candidates);
      setStep('off');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const persist = async (nutrition: IngredientNutrition) => {
    setSaving(true);
    setError(null);
    try {
      if (ingredientId) {
        await applyNutrition(ingredientId, nutrition);
      }
      onApply(nutrition);
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const pickCiqual = (c: CiqualMatchCandidate) => {
    persist({
      source: 'ciqual',
      ciqualCode: c.ciqualCode,
      offBarcode: null,
      matchedName: c.nameRaw,
      matchScore: c.score,
      matchStatus: 'reviewed',
      manualOverride: false,
      per100g: { ...c.nutrition },
      fruitVegLegumePct: c.fruitVegLegumePct,
      nutriScoreCategory: c.nutriScoreCategory,
      lastSyncedAt: new Date().toISOString(),
      lastSyncedSourceVersion: c.sourceVersion,
    });
  };

  const pickOff = (c: OffMatchCandidate) => {
    persist({
      source: 'openfoodfacts',
      ciqualCode: null,
      offBarcode: c.barcode,
      matchedName: c.brand ? `${c.nameRaw} (${c.brand})` : c.nameRaw,
      matchScore: c.score,
      matchStatus: 'reviewed',
      manualOverride: false,
      per100g: { ...c.nutrition },
      fruitVegLegumePct: null,
      nutriScoreCategory: null,
      lastSyncedAt: new Date().toISOString(),
      lastSyncedSourceVersion: null,
    });
  };

  const saveManual = () => {
    persist({
      ...(currentNutrition || emptyIngredientNutrition()),
      source: 'manual',
      matchStatus: 'manual',
      manualOverride: true,
      matchedName: ingredientName,
      matchScore: null,
      per100g: { ...manual },
      lastSyncedAt: new Date().toISOString(),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <Apple className="h-5 w-5 text-menioo-green" />
            <h3 className="font-display font-bold text-menioo-navy">Nutritional fact</h3>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Recherche pour <strong>{ingredientName}</strong>
            {currentNutrition?.matchedName ? (
              <span className="text-xs text-gray-400">
                {' '}
                — actuel : {currentNutrition.matchedName} ({currentNutrition.source})
              </span>
            ) : null}
          </p>

          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm">
              {error}
            </div>
          )}

          {(loading || saving) && (
            <div className="flex justify-center py-8">
              <Loader className="h-8 w-8 animate-spin text-menioo-green" />
            </div>
          )}

          {!loading && !saving && step === 'ciqual' && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase text-gray-400">Candidats CIQUAL</p>
              {ciqualCandidates.length === 0 && searched && (
                <p className="text-sm text-gray-500">Aucun match CIQUAL.</p>
              )}
              {ciqualCandidates.map((c) => (
                <button
                  key={c.ciqualCode}
                  type="button"
                  onClick={() => pickCiqual(c)}
                  className="w-full text-left border border-gray-200 rounded-md px-3 py-2 hover:border-menioo-green hover:bg-green-50"
                >
                  <div className="flex justify-between gap-2 text-sm">
                    <span className="font-medium text-menioo-navy">{c.nameRaw}</span>
                    <span className="shrink-0 text-gray-400">
                      {c.score}%
                      {c.preparationState && c.preparationState !== 'other'
                        ? ` · ${c.preparationState === 'raw' ? 'cru' : 'cuit'}`
                        : ''}
                    </span>
                  </div>
                  <MacroLine n={c.nutrition} />
                </button>
              ))}
              <button
                type="button"
                onClick={runOffSearch}
                className="w-full mt-2 text-sm text-menioo-navy underline"
              >
                Aucun de ces résultats → Open Food Facts
              </button>
              <button
                type="button"
                onClick={() => setStep('manual')}
                className="w-full text-sm text-gray-500 underline"
              >
                Saisie manuelle
              </button>
            </div>
          )}

          {!loading && !saving && step === 'off' && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase text-gray-400">
                Candidats Open Food Facts
              </p>
              {offCandidates.length === 0 && (
                <p className="text-sm text-gray-500">Aucun match OFF.</p>
              )}
              {offCandidates.map((c) => (
                <button
                  key={c.barcode}
                  type="button"
                  onClick={() => pickOff(c)}
                  className="w-full text-left border border-gray-200 rounded-md px-3 py-2 hover:border-menioo-green hover:bg-green-50"
                >
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-menioo-navy">
                      {c.nameRaw}
                      {c.brand ? ` — ${c.brand}` : ''}
                    </span>
                    <span className="text-gray-400">{c.score}%</span>
                  </div>
                  <MacroLine n={c.nutrition} />
                </button>
              ))}
              <button
                type="button"
                onClick={() => setStep('ciqual')}
                className="w-full mt-2 text-sm text-gray-500 underline"
              >
                ← Retour CIQUAL
              </button>
              <button
                type="button"
                onClick={() => setStep('manual')}
                className="w-full text-sm text-gray-500 underline"
              >
                Saisie manuelle
              </button>
            </div>
          )}

          {!loading && !saving && step === 'manual' && (
            <div className="space-y-3">
              <p className="text-xs font-medium uppercase text-gray-400">Saisie manuelle / 100g</p>
              {(
                [
                  ['kcal', 'kcal'],
                  ['protein_g', 'Protéines (g)'],
                  ['carbs_g', 'Glucides (g)'],
                  ['sugars_g', 'Sucres (g)'],
                  ['fat_g', 'Lipides (g)'],
                  ['saturatedFat_g', 'AG saturés (g)'],
                  ['fiber_g', 'Fibres (g)'],
                  ['salt_g', 'Sel (g)'],
                ] as const
              ).map(([key, label]) => (
                <div key={key}>
                  <label className="block text-xs text-gray-600 mb-1">{label}</label>
                  <input
                    type="number"
                    step="any"
                    value={manual[key] ?? ''}
                    onChange={(e) =>
                      setManual((m) => ({
                        ...m,
                        [key]: e.target.value === '' ? null : Number(e.target.value),
                      }))
                    }
                    className="w-full rounded-md border-gray-300 text-sm"
                  />
                </div>
              ))}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setStep('ciqual')}
                  className="px-3 py-2 text-sm border rounded-md"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={saveManual}
                  className="px-3 py-2 text-sm text-white bg-menioo-green rounded-md"
                >
                  Enregistrer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
